import {
  AI_MODE_GLYPH,
  COPILOT_GLYPH,
  type EngineGlyph,
  GEMINI_GLYPH,
  PERPLEXITY_GLYPH,
} from "./loader/engine-glyphs.js";

/**
 * How each engine Crux can ask is named and drawn: the one table every engine
 * row, filter and tile reads, and `EngineLogo` draws from.
 *
 * Which of them this deployment actually asks is not here: it is the server's
 * `CAPTURE_ENGINES`, read through `useCapturedEngines`. This table only says
 * what an engine is called once something names it, so an answer from an
 * engine since switched off still shows its own name and mark.
 */
export interface CapturedEngine {
  readonly id: string;
  readonly label: string;
  /** Where logo.dev finds its mark. Empty when it is drawn from `glyph`. */
  readonly domain: string;
  /** Its own mark, for an engine logo.dev cannot tell apart (`EngineLogo`). */
  readonly glyph?: EngineGlyph;
}

const KNOWN: readonly CapturedEngine[] = [
  { id: "chatgpt", label: "ChatGPT", domain: "chatgpt.com" },
  { id: "google_ai_overview", label: "Google AI Overview", domain: "google.com" },
  { id: "gemini", label: "Gemini", domain: "", glyph: GEMINI_GLYPH },
  { id: "google_ai_mode", label: "Google AI Mode", domain: "", glyph: AI_MODE_GLYPH },
  { id: "perplexity", label: "Perplexity", domain: "", glyph: PERPLEXITY_GLYPH },
  { id: "copilot", label: "Copilot", domain: "", glyph: COPILOT_GLYPH },
];

/** The engine by id. One not in the table prints its id, with no borrowed mark. */
export function engineMeta(engine: string, name?: string): CapturedEngine {
  return (
    KNOWN.find((entry) => entry.id === engine) ?? {
      id: engine,
      label: name ?? engine,
      domain: "",
    }
  );
}

/** An engine's name as a person reads it: "ChatGPT", never `chatgpt`. */
export function engineLabel(engine: string): string {
  return engineMeta(engine).label;
}
